import {Accordion} from '@mantine/core';
import {Card} from 'src/design/atoms/Card';
import {Center} from 'src/design/atoms/Center';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDebouncedValue, useLocalStorage} from '@mantine/hooks';
import React, {useCallback, useEffect, useMemo} from 'react';
import type {FormFieldFilterRequest} from 'src/api/ProactApiClient';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {DeleteButton} from 'src/design/molecules/DeleteButton';
import {SectionTitle} from 'src/design/atoms/Typography';
import {PendingIcon, SuccessIcon} from 'src/design/atoms/icons/StatusIcons';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {FormField} from 'src/app/forms/FormField';
import {toSingleValueField} from 'src/app/forms/singleValueField';
import {useGetFormDefinitionQuery} from 'src/redux/queries/formDefinition';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {isFilterableField} from 'src/types/FormV2Schema';
import {mapFormFieldSimpleResponseToFormFieldV2} from 'src/types/FormV2Mapping';
import {dedupeToDefinitionFields} from 'src/types/formFieldQualifiedName';

interface FilterRow {
  id: string; // Unique ID for the filter row
  fieldId: string; // Invariant ID of the field
  operator?: string;
  value?: any;
}

interface FormSubmissionsFilterProps {
  formDefinitionId: number;
  onFiltersChange: (filters: FormFieldFilterRequest[]) => void;
}

const operatorOptions: ISelectOption[] = [
  {id: 'equals', label: 'Equals'},
  {id: 'not_equals', label: 'Not Equals'},
  {id: 'contains', label: 'Contains'},
  {id: 'not_contains', label: 'Not Contains'},
];

export const FormSubmissionsFilter: React.FC<FormSubmissionsFilterProps> = ({
  formDefinitionId,
  onFiltersChange,
}) => {
  const [filters, setFilters] = useLocalStorage<FilterRow[]>({
    key: `form-submissions-filter-${formDefinitionId}`,
    defaultValue: [],
  });

  const validFilters = useMemo(() => {
    return filters.filter(f => !!f.fieldId && !!f.operator && !!f.value);
  }, [filters]);

  // Create a debounced version of the filters
  const [debouncedFilters] = useDebouncedValue(validFilters, 500, {
    leading: true,
  });

  const [isExpanded, setIsExpanded] = useLocalStorage({
    key: `form-submissions-filter-expanded`,
    defaultValue: false,
  });

  const {data: formDefinition} = useGetFormDefinitionQuery(formDefinitionId, {
    skip: !formDefinitionId,
  });

  const formFieldsRaw = useMemo(() => {
    const allMappedFields =
      formDefinition?.formFields?.map(
        mapFormFieldSimpleResponseToFormFieldV2
      ) ?? [];
    return dedupeToDefinitionFields(
      allMappedFields.filter(isFilterableField),
      allMappedFields
    ).map(
      field =>
        ({
          ...field,

          description: '',
          visibility: {},
          builderOptions: {
            ...field.builderOptions,
            referenceImageUrl: undefined,
            referenceImageKey: undefined,
          },
          ...((field.type === 'person' ||
            field.type === 'person-select-multiple') && {
            personOptions: {
              includeInactive: true,
            },
          }),
        } as IFormFieldV2)
    );
  }, [formDefinition]);

  const formFields = useMemo(
    () =>
      formFieldsRaw.map(
        field =>
          ({
            ...field,
            label: `Enter value for: ${field.label}`,
          } as IFormFieldV2)
      ),
    [formFieldsRaw]
  );

  const formFieldOptions = useMemo(
    () =>
      formFieldsRaw.map(
        field =>
          ({
            id: field.invariantId!,
            label: `${field.type.toUpperCase()} - ${field.label}`,
          } as ISelectOption)
      ),
    [formFieldsRaw]
  );

  const addFilter = useCallback(() => {
    if (formFieldsRaw.length === 0) return;

    const newFilter: FilterRow = {
      id: Math.random().toString(36).substr(2, 9),
      fieldId: formFieldsRaw[0].invariantId!,
      operator: undefined,
      value: undefined,
    };
    setFilters([...filters, newFilter]);
  }, [formFieldsRaw, filters, setFilters]);

  const removeFilter = useCallback(
    (id: string) => {
      setFilters(filters.filter(f => f.id !== id));
    },
    [filters, setFilters]
  );

  const updateFilter = useCallback(
    (id: string, updates: Partial<FilterRow>) => {
      setFilters(
        filters.map(filter =>
          filter.id === id ? {...filter, ...updates} : filter
        )
      );
    },
    [filters, setFilters]
  );

  // Update to use debounced value
  useEffect(() => {
    const filterRequests = debouncedFilters.map(
      ({fieldId, operator, value}) =>
        ({
          fieldInvariantId: fieldId,
          operand: operator!,
          value,
        } as FormFieldFilterRequest)
    );
    onFiltersChange(filterRequests);
  }, [debouncedFilters]);

  const renderAddFilterSection = useCallback(() => {
    return (
      <Stack mt="xl" mb="md">
        <CreateButton onClick={addFilter}>Add Filter</CreateButton>

        <Text size="xs" c="dimmed">
          Click to add a new field-based filter to narrow down your search.
        </Text>
      </Stack>
    );
  }, [addFilter]);

  const renderFilterHeader = useCallback(
    (filter: FilterRow, validField: boolean) => {
      return (
        <Group justify="space-between" mb="xs">
          <Group gap="xs">
            {validField ? (
              <>
                <SuccessIcon />
                <Text size="xs" c="green">
                  Filter applied
                </Text>
              </>
            ) : (
              <>
                <PendingIcon />
                <Text size="xs" c="dimmed">
                  Choose a field, operator, and value to apply filter
                </Text>
              </>
            )}
          </Group>
          <DeleteButton onClick={() => removeFilter(filter.id)} size="xs">
            Delete Filter
          </DeleteButton>
        </Group>
      );
    },
    [removeFilter]
  );

  const renderFilterContent = useCallback(() => {
    const span = {base: 12, lg: 6, xl: 4};
    return (
      <Grid gutter="sm">
        {filters.map(filter => {
          const selectedField = formFields.find(
            f => f.invariantId === filter.fieldId
          );

          const isValidField =
            validFilters.filter(f => f.id === filter.id).length > 0;

          return (
            <Grid.Col span={span} key={filter.id}>
              <Card withBorder h="100%">
                {renderFilterHeader(filter, isValidField)}

                <SelectBox
                  label="Field"
                  options={formFieldOptions}
                  value={filter.fieldId}
                  onSelectChangeCallback={value =>
                    updateFilter(filter.id, {
                      fieldId: value,
                      value: null,
                    })
                  }
                />

                <SelectBox
                  label="Operator"
                  options={operatorOptions}
                  value={filter.operator}
                  onSelectChangeCallback={value =>
                    updateFilter(filter.id, {operator: value})
                  }
                />

                {selectedField && (
                  <Grid style={{width: '100%'}}>
                    <FormField
                      field={toSingleValueField(selectedField)}
                      value={filter.value}
                      onChange={(_, value) =>
                        updateFilter(filter.id, {value: value})
                      }
                    />
                  </Grid>
                )}
              </Card>
            </Grid.Col>
          );
        })}

        <Grid.Col span={12}>
          <Center>{renderAddFilterSection()}</Center>
        </Grid.Col>
      </Grid>
    );
  }, [
    filters,
    formFields,
    formFieldOptions,
    validFilters,
    renderFilterHeader,
    renderAddFilterSection,
    updateFilter,
  ]);

  const renderAccordionHeader = useCallback(() => {
    return (
      <Group justify="space-between" pr="md">
        <SectionTitle mb={0} text="Field-Based Filters" />
        {filters.length > 0 && (
          <Text size="sm" c="dimmed">
            {validFilters.length} filter
            {validFilters.length !== 1 ? 's' : ''} applied
          </Text>
        )}
      </Group>
    );
  }, [filters, validFilters]);

  return (
    <Accordion
      mb="md"
      variant="contained"
      value={isExpanded ? 'filters' : null}
      onChange={value => setIsExpanded(value === 'filters')}>
      <Accordion.Item value="filters">
        <Accordion.Control>{renderAccordionHeader()}</Accordion.Control>
        <Accordion.Panel>
          <Text size="xs" c="dimmed" mb="xs">
            Add a field-based filter to narrow down your search. Field-based
            filters search form submissions for fields with matching values and
            returns results that meet all active filters. The filter shows the
            same field the user sees, allowing you to select a possible value as
            the user would.
          </Text>
          {renderFilterContent()}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
